import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { DocumentFiltersV2 } from '../../components/DocumentFilter/DocumentFilterV2';
import { DocumentFilterV2 } from '../../components/DocumentFilter/DocumentFilterV2';
import { DocumentItem } from '../../components/DocumentItem/DocumentItem';
import Loader from '../../components/Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  removeShallowFavorites,
  selectDocumentGroupsWithFilter,
  selectDocumentsIsListLoading,
  selectFiles,
  selectFilesFoldersSortedFiltered,
  selectActiveTab,
  setActiveTab,
  updateFav,
} from './documentsSlice';
import { fetchFolders } from './documentsSlice/actionCreators';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EmptyStar } from '../../assets/icons/star.svg';
import type { OptionItemProps } from '../../components/Options/Options';
import { selectUserPermissions } from '../Login/LoginSlice';
import { refreshUserPermissions } from '../Login/LoginSlice/actionCreators';
import {
  getGroupsOrganizedByAccount,
  getOnFavChangeAlertTx,
  isAtLeastOneInstanceFileOffline,
} from './helpers';
import NewDocIcon from '../../assets/icons/file-with-plus.svg';
import NewFolderIcon from '../../assets/icons/folder-with-plus.svg';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import {
  LocalStorageFiltersKeys,
  checkIfFiltersApplied,
  useLocalStorageFilters,
} from '../../utils/customHooks/useLocalStorageFilters';
import { handleSubmissions } from '../EditDocuments/EditDocuments';
import {
  SEmptyListFallback,
  SFloatingDocumentAddButton,
  SHiddenInput,
  SList,
  SOptions,
  SPage,
  SSearchFilterBar,
  SSlider,
  STab,
  STabContainer,
  STabList,
  STabPanel,
  STabs,
} from './styles';
import { toggleFavoriteStatusWithFallback } from '../../apis/mediaAPI';
import type { AlertBannerRef } from '../../components/AlertBanner/AlertBanner';
import { AlertBanner } from '../../components/AlertBanner/AlertBanner';
import { generateInitialSelectedData } from '../../components/DocumentFilter/helpers';
import {
  DocumentSortingType,
  DocumentsSortingOptions,
} from '../../components/DocumentsSortingOptions/DocumentsSortingOptions';
import { FilesAndFoldersOptions } from '../../components/FilesAndFoldersOptions/FilesAndFoldersOptions';
import type { OperationListSelectModalRef } from '../../components/OperationListSelectModal/OperationListSelectModal';
import { OperationListSelectModal } from '../../components/OperationListSelectModal/OperationListSelectModal';
import { translate } from '../../utils/translate';
import type { BrowseFolderOptionsStateType } from '../BrowseFolder/BrowseFolder';
import { generateOptionsV2, getFileOrFolderInfoByIdV2 } from '../BrowseFolder/BrowseFolder.helpers';
import type { DocumentGroup, FileItem, FolderItem } from './documentsSlice/types';
import { getNumbersBasedDateTextByLanguage } from '../../utils/date';
import { ActiveDocumentsTab } from '../../utils/enums';

const generateDocumentDefaultFilters = (isSeeOrgGroups: boolean | undefined): DocumentFiltersV2 => {
  return {
    showEmptyFolders: false,
    showFilesOnly: false,
    selectedData: 'UNSELECTED_ALL',
    memberFilter: isSeeOrgGroups ? ['MEMBER'] : undefined,
  };
};

type IDocumentsProps = {
  isHeaderOptionsOpen: boolean;
  setHeaderOptionsOpen: Dispatch<SetStateAction<boolean>>;
};

export type EnhancedFolderItem = FolderItem & { name: string; id: number; subName?: string };

export const Documents = (props: IDocumentsProps) => {
  const { isHeaderOptionsOpen, setHeaderOptionsOpen } = props;

  const inputFile = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const confirm = useConfirmation();

  const userPermissions = useAppSelector(selectUserPermissions);
  const activeTab = useAppSelector(selectActiveTab);
  const mayInteractWithAllGroups = userPermissions?.groups.mayInteractWithAll;

  const tabIndex = useMemo(() => (activeTab === ActiveDocumentsTab.Favorites ? 1 : 0), [activeTab]);
  const defaultFilters = useMemo(
    () => generateDocumentDefaultFilters(mayInteractWithAllGroups),
    []
  );

  const [filters, setFilters] = useLocalStorageFilters<DocumentFiltersV2>(
    LocalStorageFiltersKeys.documents,
    { ...defaultFilters }
  );
  const [stagedFilter, setStagedFilters] = useState<DocumentFiltersV2>({ ...filters });

  const [sorting, setSorting] = useLocalStorageFilters<{ selected: DocumentSortingType }>(
    LocalStorageFiltersKeys.documentsSorting,
    { selected: DocumentSortingType.NewToOld }
  );
  const { selected: sortBy } = sorting;
  const setSortBy = (_sorting: DocumentSortingType) => {
    setSorting({ selected: _sorting });
  };

  const documentGroups = useAppSelector(selectDocumentGroupsWithFilter(stagedFilter));
  const allFiles = useAppSelector(selectFiles);

  const { itemsList } = useAppSelector(
    selectFilesFoldersSortedFiltered({
      sortBy,
      searchTerm,
      filters:
        activeTab === ActiveDocumentsTab.Favorites
          ? {
              showEmptyFolders: true,
              showFilesOnly: false,
            }
          : filters,
      showDocumentsFromFolders: activeTab === ActiveDocumentsTab.Favorites,
      /**
       * Note, Cosafe-backend does not provide group ids for documents in folders.
       * To guarantee that documents in folders are visible in the Favorites tab, we
       * need to prevent filtering on groups here by settting the groups to undefined.
       * After we have migrated to the new backend, the check on activeTab here can
       * most likely be removed.
       * See more details in: https://github.com/cosafe/web/issues/1316
       */
      groups:
        activeTab !== ActiveDocumentsTab.Favorites && filters.memberFilter?.length === 1
          ? documentGroups
          : undefined,
    })
  );
  const isLoading = useAppSelector(selectDocumentsIsListLoading);

  useEffect(() => {
    if (!userPermissions) {
      dispatch(refreshUserPermissions());
    }
    dispatch(fetchFolders());
    // do we need this performance guard ?
    // if (documentGroups.length === 0) dispatch(fetchFolders());
  }, []);

  useEffect(() => {
    if (activeTab === ActiveDocumentsTab.Documents) {
      dispatch(removeShallowFavorites());
    }
  }, [activeTab]);

  // OPTIONS MANAGMENT START ================================================================
  const operationExtraIdsSelectRef =
    useRef<OperationListSelectModalRef<DocumentGroup | EnhancedFolderItem>>(null);
  const [selected, setSelected] = useState<FileItem | FolderItem>();
  const [optionsState, setOptionsState] = useState<
    BrowseFolderOptionsStateType & { isHeaderOptionOpen?: boolean }
  >({
    isOpen: false,
    isHeaderOptionOpen: false,
  });
  const setOptionsOpen = (isOpen: boolean) => {
    setOptionsState(prev => ({ ...prev, isOpen }));
    // setHeaderOptionsOpen(isOpen);
  };
  const setIsHeaderOptionsOpen = (_isHeaderOptionOpen: boolean) => {
    setOptionsState(prev => ({ ...prev, isHeaderOptionOpen: _isHeaderOptionOpen }));
    setHeaderOptionsOpen(_isHeaderOptionOpen);
  };

  // header options start
  const handleManageOffline = () => {
    navigation('edit/manageOffline');
    setHeaderOptionsOpen(false);
  };
  useEffect(() => {
    if (isHeaderOptionsOpen) {
      setOptionsState(prev => ({
        ...prev,
        isHeaderOptionOpen: true,
        options: [
          {
            name: 'documents_manage_offline_files',
            callback: handleManageOffline,
          },
        ],
      }));
    }
  }, [isHeaderOptionsOpen]);

  useEffect(() => {
    return () => {
      setOptionsState({
        isOpen: false,
      });
    };
  }, []);
  // header options end

  const handleMoreOptionClick = (item: FolderItem | FileItem) => () => {
    setSelected(item);
    generateOptionsV2({
      dependencies: {
        confirm,
        dispatch,
        navigation,
        setOptionsState,
        extraOperationRef: operationExtraIdsSelectRef,
      },
      documentPermissions: userPermissions?.documents,
      selected: item,
    });
  };

  const handleCreateFolder = (text: string | undefined) => {
    navigation(`edit/createFolder/${text}`);
  };
  const onAddDocumentClick = () => inputFile.current?.click();
  const onAddFolderClick = () => {
    confirm({
      title: 'documents_name_folder',
      onSubmit: text => {
        handleCreateFolder(text);
      },
      onCancel: () => {},
      confirmText: 'messages_proceed',
      inputBox: true,
      placeholderTx: 'documents_folder_name',
      confirmStyle: 'standard',
    });
  };

  // OPTIONS MANAGMENT END ================================================================
  const toggleFilterOpen = () => setFilterOpen(prev => !prev);

  const openAddModal = () => {
    const callbackWrapper = (addItemF: () => void) => {
      setOptionsState(prev => ({ ...prev, isHeaderOptionOpen: false }));
      addItemF();
    };

    setOptionsState(prev => ({
      ...prev,
      isHeaderOptionOpen: true,
      options: [
        {
          name: 'documents_add_new_doc',
          icon: NewDocIcon,
          callback: () => callbackWrapper(onAddDocumentClick),
        },
        {
          name: 'documents_add_new_folder',
          icon: NewFolderIcon,
          callback: () => callbackWrapper(onAddFolderClick),
        },
      ] as OptionItemProps[],
    }));
  };

  // OPTIONS MANAGMENT END ================================================================

  const alertRef = useRef<AlertBannerRef>(null);

  const onFilter = (_filters: DocumentFiltersV2) => {
    setFilters(_filters);
    setStagedFilters(_filters);

    setFilterOpen(false);
  };

  const onChange = (_filters: DocumentFiltersV2) => {
    setStagedFilters(prev => ({
      ...prev,
      memberFilter: _filters.memberFilter,
    }));
  };
  const resetStaged = () => {
    setStagedFilters({
      memberFilter: filters.memberFilter,
      showEmptyFolders: filters.showEmptyFolders,
      selectedData: filters.selectedData,
      showFilesOnly: filters.showFilesOnly,
    });
  };
  const onSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleFolderRedirect = (groupID: number) => (id: number) => navigation(`${groupID}/${id}`);

  const handleFavClick = async (_id: number, newFavValue: boolean, isFolder = false) => {
    toggleFavoriteStatusWithFallback({
      id: _id,
      newStatus: newFavValue,
      onFallback: (__id, revertedValue, _isFolder) => {
        dispatch(
          updateFav({
            id: __id,
            newFavValue: revertedValue,
            isFolder: _isFolder,
            isFallbackUpdate: true,
          })
        );
      },
      onSuccess: () => {
        alertRef.current?.alert(translate(getOnFavChangeAlertTx(newFavValue, isFolder)));
      },
      isFolder,
    });
    dispatch(updateFav({ id: _id, newFavValue, isFolder }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSubmissions(event);
    navigation('edit/addDocument');
  };

  const renderFolderOrFile = (item: FileItem | FolderItem, index: number) => {
    if ((item as FolderItem).GroupID) {
      const folderItem = item as FolderItem;
      return (
        <DocumentItem
          key={`${folderItem.ID}-${index}`}
          iconVariant={folderItem.Offline ? 'OFFLINE_FOLDER' : 'FOLDER'}
          title={folderItem.Name}
          onClick={handleFolderRedirect(folderItem.GroupID)}
          itemId={folderItem.ID}
          subTitle={folderItem.groupName}
          onMoreOptionClick={handleMoreOptionClick(item)}
          favorite={folderItem.favorited}
          onFavClick={(_id, newValue) => handleFavClick(_id, newValue, true)}
          searchTerm={searchTerm}
        />
      );
    } else {
      const fileItem = item as FileItem;

      return (
        <DocumentItem
          key={`${fileItem.id}-${index}`}
          iconVariant={isAtLeastOneInstanceFileOffline(fileItem) ? 'OFFLINE_FILE' : 'FILE'}
          title={fileItem.original_filen_name}
          pdfLink={fileItem.document_url}
          subTitle={`${translate('updated_at')} ${getNumbersBasedDateTextByLanguage({
            dateText: fileItem.upload_time,
          })}`}
          itemId={fileItem.id}
          onMoreOptionClick={handleMoreOptionClick(item)}
          onFavClick={handleFavClick}
          favorite={fileItem.favorite}
          searchTerm={searchTerm}
        />
      );
    }
  };
  const initialMemberFilter = useMemo(
    () => (mayInteractWithAllGroups ? new Set(filters.memberFilter) : undefined),
    [filters.memberFilter, mayInteractWithAllGroups]
  );

  // renders
  if (isLoading) return <Loader />;

  return (
    <SPage>
      <STabs
        selectedTabClassName="is-selected"
        selectedTabPanelClassName="is-selected"
        defaultIndex={tabIndex}
        role="documentsTabs"
      >
        <STabContainer role="tabsContainer">
          <STabList $selectedIndex={tabIndex}>
            <STab onClick={() => dispatch(setActiveTab(ActiveDocumentsTab.Documents))}>
              {translate('documents_all_files')}
            </STab>
            <STab onClick={() => dispatch(setActiveTab(ActiveDocumentsTab.Favorites))}>
              {
                <>
                  <div className="svgContainer" role="iconContainer">
                    <EmptyStar className="starSVG" role="icon" />
                  </div>
                  <p role="favoritesText">{translate('documents_favorites')}</p>
                </>
              }
            </STab>
          </STabList>
          <SSlider className="slider" role="slider">
            <div className="indicator" role="sliderIndicator" />
          </SSlider>
        </STabContainer>
        <AlertBanner ref={alertRef} />
        <SSearchFilterBar
          onSearch={onSearch}
          handleOpenFilter={toggleFilterOpen}
          withoutFilterButton={
            !userPermissions?.documents.mayAdd || activeTab === ActiveDocumentsTab.Favorites
          }
          isFilterApplied={checkIfFiltersApplied({
            defaultFilters: generateDocumentDefaultFilters(mayInteractWithAllGroups),
            filters,
          })}
          value={searchTerm}
        />

        <DocumentFilterV2
          defaultFilters={defaultFilters}
          data={documentGroups}
          displayData={getGroupsOrganizedByAccount(documentGroups)}
          isOpen={filterOpen}
          setIsOpen={setFilterOpen}
          onFilter={onFilter}
          onChange={onChange}
          resetStaged={resetStaged}
          initialShowFilesOnly={filters.showFilesOnly}
          initialShowEmptyFolders={filters.showEmptyFolders}
          initialMemberFilter={initialMemberFilter}
          initialSelectedData={generateInitialSelectedData(filters.selectedData, documentGroups)}
        />
        <DocumentsSortingOptions onSort={setSortBy} initialSort={sortBy} />
        <SEmptyListFallback
          listLength={itemsList.length}
          isLoading={isLoading}
          searchTerm={searchTerm}
          $searchTerm={!!searchTerm}
          emptyListTx={'documents_empty_list'}
          emptySearchTx={'document-empty-search'}
          ignoreSearchTermInFallbackText
        />
        <STabPanel>
          {itemsList.length !== 0 && (
            <SList role="documentsList">
              {itemsList.map((folderOrFile, index) => renderFolderOrFile(folderOrFile, index))}
            </SList>
          )}
        </STabPanel>
        <STabPanel>
          {itemsList.length !== 0 && (
            <SList role="favoritesList">
              {itemsList
                .filter((folderOrFile: FolderItem | FileItem) => {
                  if ((folderOrFile as FolderItem).GroupID) {
                    return (folderOrFile as FolderItem).favorited || folderOrFile.shallowFavorite;
                  } else {
                    return (folderOrFile as FileItem).favorite || folderOrFile.shallowFavorite;
                  }
                })
                .map((folderOrFile, index) => renderFolderOrFile(folderOrFile, index))}
            </SList>
          )}
        </STabPanel>
      </STabs>

      {selected && optionsState.options && (
        <FilesAndFoldersOptions
          items={optionsState.options}
          isOpen={optionsState.isOpen}
          setIsOpen={setOptionsOpen}
          withoutOptions={!userPermissions?.documents.mayAdd}
          setTabBar
          info={getFileOrFolderInfoByIdV2(allFiles, selected)}
        />
      )}
      {userPermissions?.documents.mayManageOffline && optionsState.options && (
        <SOptions
          items={optionsState.options}
          isOpen={!!optionsState.isHeaderOptionOpen}
          setIsOpen={setIsHeaderOptionsOpen}
          setTabBar
        />
      )}
      {userPermissions?.documents.mayAdd && !optionsState.isOpen && (
        <SFloatingDocumentAddButton onClick={openAddModal} />
      )}
      {/* adding document input  */}
      <SHiddenInput
        type="file"
        id="file"
        accept=".pdf"
        ref={inputFile}
        onChange={handleInputChange}
      />
      <OperationListSelectModal ref={operationExtraIdsSelectRef} />
    </SPage>
  );
};
